/* Optional presentation adapter. The existing Studio owns design state and history. */
(function (global) {
  'use strict';
  if (global.TodayMerchPersonalizationExperience) return;
  const instances = new WeakMap();
  const events = Object.freeze([
    'todaymerch:marking-state-change', 'todaymerch:marking-zone-change', 'todaymerch:product-design-transform-change',
    'todaymerch:product-design-measurements-change', 'todaymerch:artwork-production-source-change',
    'todaymerch:artwork-compatibility-change', 'todaymerch:personalization-selection-change',
    'todaymerch:artwork-quote-change', 'todaymerch:artwork-color-adaptation-request',
    'todaymerch:personalization-experience-change',
  ]);
  const copy = value => typeof value === 'string' ? value.slice(0, 600) : '';
  const setText = (element, value) => { const next = copy(value); if (element.textContent !== next) element.textContent = next; };
  function element(tag, className, text) {
    const node = document.createElement(tag); node.className = className;
    if (text) node.textContent = text;
    return node;
  }
  function button(label, action) {
    const node = element('button', 'personalization-experience__button', label);
    node.type = 'button'; node.dataset.experienceAction = action; return node;
  }
  const errors = Object.freeze({
    network: 'La connexion a été interrompue. Votre design est conservé. Réessayez quand vous êtes connecté.',
    stale: 'Votre design a changé entre-temps. Vérifiez la version actuelle avant de réessayer.',
    storage: 'La sauvegarde n’a pas abouti. Gardez cette page ouverte et réessayez.',
    unsupported: 'Cette action n’est pas disponible ici. Votre design reste inchangé.',
  });
  const diagnosticLabels = Object.freeze({
    unknown: 'Vérification à compléter', pending: 'Vérification en cours…',
    attention: 'Un point à vérifier', blocked: 'Une adaptation est nécessaire pour ce choix',
    checked: 'Contrôles disponibles effectués', error: 'Vérification indisponible',
  });

  function create({ root, mount, adapter, steps = [], events: observedEvents = events } = {}) {
    if (!root?.addEventListener || !mount?.append || !root.contains(mount)) throw new TypeError('Le montage doit appartenir à la fiche produit.');
    if (typeof adapter?.read !== 'function' || typeof adapter?.execute !== 'function') throw new TypeError('Un adaptateur de lecture et de commande est requis.');
    if (instances.has(root)) return instances.get(root);
    let destroyed = false, pending = null, generation = 0, announcementTimer, lastDiagnosticMessage = null, readFailed = false;
    const lifetime = new AbortController();
    const on = (node, name, callback) => node.addEventListener(name, callback, { signal: lifetime.signal });
    const section = element('section', 'personalization-experience');
    section.setAttribute('aria-label', 'Suivi de votre personnalisation');
    section.dataset.personalizationExperienceControls = '';
    const navigation = element('nav', 'personalization-experience__steps');
    navigation.setAttribute('aria-label', 'Modifier une étape');
    const targets = new Map();
    for (const step of steps) {
      if (!step?.id || !step.target || targets.has(step.id)) continue;
      targets.set(step.id, step.target);
      const control = button(copy(step.label), `step:${step.id}`);
      on(control, 'click', () => focusStep(step.id)); navigation.append(control);
    }
    navigation.hidden = targets.size === 0;
    const toolbar = element('div', 'personalization-experience__actions');
    toolbar.setAttribute('role', 'group'); toolbar.setAttribute('aria-label', 'Historique et sauvegarde');
    const undo = button('Annuler', 'undo'), redo = button('Rétablir', 'redo'), save = button('Enregistrer', 'save');
    const history = element('p', 'personalization-experience__history');
    toolbar.append(undo, redo, save);
    const persistence = element('p', 'personalization-experience__persistence');
    persistence.dataset.experiencePersistence = '';
    const draft = element('div', 'personalization-experience__draft');
    const draftText = element('p', 'personalization-experience__draft-text');
    const resume = button('Reprendre', 'resume'), dismiss = button('Ignorer ce brouillon', 'dismiss-draft');
    draft.append(draftText, resume, dismiss); draft.hidden = true;
    const preview = element('p', 'personalization-experience__preview');
    preview.dataset.experiencePreview = '';
    const diagnostic = element('div', 'personalization-experience__diagnostic');
    const diagnosticTitle = element('p', 'personalization-experience__diagnostic-title');
    const diagnosticDetail = element('p', 'personalization-experience__diagnostic-detail');
    diagnosticTitle.dataset.experienceDiagnostic = '';
    const retry = button('Réessayer la vérification', 'retry-diagnostic');
    diagnostic.append(diagnosticTitle, diagnosticDetail, retry);
    const adaptation = element('div', 'personalization-experience__adaptation');
    const adaptationText = element('p', 'personalization-experience__adaptation-text');
    const accept = button('Accepter cette adaptation', 'accept-adaptation');
    const original = button('Garder mon original', 'keep-original');
    adaptation.append(adaptationText, accept, original); adaptation.hidden = true;
    const review = element('p', 'personalization-experience__review');
    review.dataset.experienceReview = '';
    const error = element('p', 'personalization-experience__error');
    error.setAttribute('role', 'alert'); error.hidden = true;
    const live = element('p', 'personalization-experience__live');
    live.setAttribute('role', 'status'); live.setAttribute('aria-live', 'polite'); live.setAttribute('aria-atomic', 'true');
    section.append(navigation, toolbar, history, persistence, draft, preview, diagnostic, adaptation, review, error, live);
    mount.append(section);

    function announce(message) {
      clearTimeout(announcementTimer);
      announcementTimer = setTimeout(() => { if (!destroyed) setText(live, message); }, 80);
    }
    function read() {
      const value = adapter.read();
      if (!value || typeof value.revision !== 'string' || !value.revision || typeof value.contextKey !== 'string' || !value.contextKey) throw new TypeError('A revision and complete context binding are required.');
      return value;
    }
    function bound(result, state) { return result?.revision === state.revision && result?.contextKey === state.contextKey; }
    function proposalReady(proposal) {
      return !!copy(proposal?.proposalRevision) && !!copy(proposal?.proposalId) && /^[a-f0-9]{64}$/i.test(proposal?.proposalSha256 || '');
    }
    function available(control, enabled) {
      // Keep a focused control in the tab order when state changes underneath it.
      control.setAttribute('aria-disabled', String(!enabled));
    }
    function refresh() {
      if (destroyed) return;
      let state;
      try { state = read(); } catch {
        readFailed = true;
        const lostFocus = document.activeElement;
        setText(error, 'Le suivi de votre design est indisponible. Gardez cette page ouverte.'); error.hidden = false;
        for (const control of [undo, redo, save, resume, dismiss, retry, accept, original]) available(control, false);
        setText(diagnosticTitle, diagnosticLabels.unknown);
        setText(diagnosticDetail, ''); adaptation.hidden = true;
        if (section.contains(lostFocus) && lostFocus.closest('[hidden]')) save.focus({ preventScroll: true });
        return;
      }
      if (readFailed) { error.hidden = true; readFailed = false; }
      const wasFocused = document.activeElement;
      const busy = !!pending;
      section.setAttribute('aria-busy', String(busy));
      available(undo, !busy && state.history?.canUndo === true);
      available(redo, !busy && state.history?.canRedo === true);
      available(save, !busy); available(resume, !busy); available(dismiss, !busy);
      available(retry, !busy); available(accept, !busy); available(original, !busy);
      setText(history, state.history?.label || 'Votre original reste conservé.');
      setText(persistence, state.persistence?.message || 'Modifications non enregistrées');
      draft.hidden = state.draft?.available !== true;
      setText(draftText, state.draft?.label || 'Un brouillon est disponible. Reprenez-le pour retrouver vos choix.');
      setText(preview, state.preview?.message || 'Aperçu de placement');
      const currentDiagnostic = bound(state.diagnostic, state) ? state.diagnostic : null;
      const diagnosticState = Object.hasOwn(diagnosticLabels, currentDiagnostic?.state) ? currentDiagnostic.state : 'unknown';
      diagnostic.dataset.state = diagnosticState;
      setText(diagnosticTitle, diagnosticLabels[diagnosticState]);
      setText(diagnosticDetail, currentDiagnostic?.message || 'Les contrôles ne sont pas encore disponibles pour cette version.');
      retry.hidden = diagnosticState !== 'error';
      const proposal = bound(state.adaptation, state) ? state.adaptation : null;
      adaptation.hidden = !proposal || !['proposed', 'accepted'].includes(proposal.state);
      setText(adaptationText, proposal?.message || (proposal?.state === 'accepted' ? 'Adaptation acceptée pour cette version.' : 'Une adaptation est proposée. Votre original est conservé.'));
      accept.hidden = proposal?.state !== 'proposed' || !proposalReady(proposal);
      original.hidden = proposal?.state !== 'proposed';
      // A browser diagnostic never upgrades production review.
      setText(review, state.review?.message || 'Validation finale non effectuée');
      const diagnosticMessage = diagnosticTitle.textContent + '. ' + diagnosticDetail.textContent;
      if (lastDiagnosticMessage !== null && lastDiagnosticMessage !== diagnosticMessage) announce(diagnosticMessage);
      lastDiagnosticMessage = diagnosticMessage;
      if (section.contains(wasFocused) && wasFocused.closest('[hidden]')) save.focus({ preventScroll: true });
    }
    async function command(type, payload = {}) {
      if (destroyed || typeof type !== 'string' || !type) return false;
      let state;
      try { state = read(); } catch { refresh(); return false; }
      if ((type === 'undo' && state.history?.canUndo !== true) || (type === 'redo' && state.history?.canRedo !== true)
        || (['resume', 'dismiss-draft'].includes(type) && state.draft?.available !== true)) return false;
      if (['accept-adaptation', 'keep-original'].includes(type)) {
        if (!bound(state.adaptation, state) || state.adaptation.state !== 'proposed' || (type === 'accept-adaptation' && !proposalReady(state.adaptation))) return false;
        payload = { ...payload, proposalRevision: state.adaptation.proposalRevision, proposalId: state.adaptation.proposalId, proposalSha256: state.adaptation.proposalSha256 };
      }
      pending?.abort();
      const request = new AbortController(), token = ++generation;
      pending = request; error.hidden = true; refresh();
      try {
        // Invoke synchronously before awaiting so native upload pickers keep user activation.
        const result = await adapter.execute({ type, payload, expectedRevision: state.revision, contextKey: state.contextKey }, { signal: request.signal });
        if (destroyed || request.signal.aborted || token !== generation) return false;
        pending = null; refresh();
        if (result === false) return false;
        const current = read();
        const acceptedCurrentProposal = bound(current.adaptation, current) && current.adaptation?.state === 'accepted'
          && current.adaptation.proposalId === payload.proposalId && current.adaptation.proposalSha256 === payload.proposalSha256;
        announce(type === 'save' ? (read().persistence?.message || 'Sauvegarde terminée.')
          : type === 'undo' ? 'Dernière modification annulée.' : type === 'redo' ? 'Modification rétablie.'
          : type === 'accept-adaptation' ? (acceptedCurrentProposal ? 'Adaptation acceptée. La vérification doit correspondre à cette version.' : 'Consultez la version actuelle de votre personnalisation.')
          : type === 'resume' ? 'Brouillon repris. Les vérifications sont à actualiser.' : 'Votre choix a été pris en compte.');
        return true;
      } catch (failure) {
        if (destroyed || request.signal.aborted || token !== generation) return false;
        pending = null; refresh();
        setText(error, errors[failure?.code] || 'Cette action n’a pas abouti. Votre design est conservé. Réessayez.');
        error.hidden = false; return false;
      }
    }
    function focusStep(id) {
      const target = targets.get(id);
      if (destroyed || !target?.isConnected || target.hidden || target.closest('[hidden],[inert]')) return false;
      const visible = node => node?.isConnected && !node.disabled && node.getAttribute('aria-disabled') !== 'true'
        && !node.closest('[hidden],[inert]') && node.getClientRects().length > 0
        && getComputedStyle(node).visibility !== 'hidden';
      const selector = 'button,input:not([type="hidden"]),select,textarea,a[href],[tabindex]';
      if (!visible(target)) return false;
      const focusable = target.matches(selector) && visible(target) ? target
        : [...target.querySelectorAll(selector)].find(visible);
      const destination = focusable || target;
      if (!focusable && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      destination.focus({ preventScroll: true });
      if (document.activeElement !== destination) return false;
      const rect = destination.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > global.innerHeight) destination.scrollIntoView({ block: 'center', behavior: 'auto' });
      return true;
    }
    for (const control of [undo, redo, save, resume, dismiss, retry, accept, original]) {
      on(control, 'click', () => {
        if (control.getAttribute('aria-disabled') !== 'true') void command(control.dataset.experienceAction);
      });
    }
    on(root, 'keydown', event => {
      if (event.defaultPrevented || event.altKey || !(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
      if (event.target.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"]),dialog')) return;
      const control = event.shiftKey ? redo : undo;
      if (control.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault(); void command(event.shiftKey ? 'redo' : 'undo');
    });
    for (const name of new Set(observedEvents)) on(root, name, refresh);
    const api = Object.freeze({ refresh, command, focusStep, destroy() {
      if (destroyed) return;
      destroyed = true; generation++; pending?.abort(); clearTimeout(announcementTimer);
      lifetime.abort(); section.remove(); instances.delete(root);
    } });
    instances.set(root, api); refresh(); return api;
  }
  global.TodayMerchPersonalizationExperience = Object.freeze({ create, events });
})(window);
